import { Badge } from '@heidi/design-system/badge';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { ExternalLink } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { clinicianUpgradeCheckoutMessages } from '../../../../trials-and-upgrades/clinician-upgrade-checkout/clinician-upgrade-checkout.messages';
import { useClinicianSeatPrices } from '../../../../trials-and-upgrades/clinician-upgrade-checkout/use-clinician-seat-prices';
import {
  COMPLIMENTARY_TRIAL_END_YEARLY_DISCOUNT_PERCENT,
  ComplimentaryTrialEndOfferCard,
} from '../../../../trials-and-upgrades/complimentary-trial-end/complimentary-trial-end-offer-card';
import { heidi2UpgradeCheckoutMessages } from '../../../../trials-and-upgrades/heidi-2-upgrade-checkout/heidi-2-upgrade-checkout.messages';
import { PlanPeriodTabs } from '../../../../trials-and-upgrades/plan-period-tabs/plan-period-tabs';
import type { BillingPeriod } from '../../../../trials-and-upgrades/plan-period-tabs/types';
import { practiceSeatUpgradeCardMessages } from '../../../../trials-and-upgrades/practice-seat-upgrade-card/practice-seat-upgrade-card.messages';
import { useComplimentaryYearlyMaxOffer } from '../../../use-complimentary-yearly-max-offer';
import {
  getCommerceBillingUiPorts,
  type CommerceBillingUiPorts,
} from '../../billing-ui-ports';
import { usePracticeSeatPrices } from '../../hooks/usePracticeSeatPrices';
import { usePricingPlansModalFeatures } from './pricing-plans-modal.features';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';
import {
  TrialEndDualSeatPlanCard,
  YearlyOnlyPill,
} from './trial-end-dual-seat-plan-card';
import { trialEndPricingPlanCardsMessages } from './trial-end-pricing-plan-cards.messages';
import { usePricingPlanColumns } from './use-pricing-plan-columns';
import { usePricingPlansModalCtas } from './usePricingPlansModalCtas';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

const AppleIapInfoText = (
  props: ComponentProps<CommerceBillingUiPorts['AppleIapInfoText']>
) => {
  const Component = getCommerceBillingUiPorts().AppleIapInfoText;
  return <Component {...props} />;
};

function useSeatPriceRichText() {
  const strong = (chunks: ReactNode[]) => (
    <span className={typographyVariant({ variant: 'p2Bold' })}>{chunks}</span>
  );
  const original = (chunks: ReactNode[]) => (
    <s className="text-text-tertiary">{chunks}</s>
  );
  return { strong, original };
}

export function TrialEndPricingPlanCards({
  modalState,
}: {
  modalState: UsePricingPlansModalStateReturn;
}) {
  const { locale, formatMessage } = useIntl();
  const { strong, original } = useSeatPriceRichText();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const isYearly = billingPeriod === 'annual';

  const {
    isPending,
    currentPlan,
    isTrialing,
    isTrialingClinician,
    isClinicianStandardPlan,
    isPracticeStandardPlan,
    legacyTier,
  } = modalState;

  const ports = getCommerceBillingUiPorts();
  const yearlyMaxOffer = useComplimentaryYearlyMaxOffer({
    isHeidi2OptInEnabled: ports.useIsHeidi2OptInEnabled(),
    isTrialEndEnabled: ports.useIsHeidi2TrialEndEnabled(),
    isTrialing,
  });
  const showComplimentaryOffer = yearlyMaxOffer != null;

  const {
    trialEndClinicianProPlanCtaLabel,
    clinicianProPlusPlanCtaLabel,
    onTrialEndClinicianCtaClick,
    onTrialEndPracticeCtaClick,
    onTrialEndPeriodChange,
    onTalkToSalesClick,
    isAppleCompliant,
  } = usePricingPlansModalCtas(modalState);
  const handlePeriodChange = (nextPeriod: BillingPeriod) => {
    if (nextPeriod === billingPeriod) return;
    setBillingPeriod(nextPeriod);
    onTrialEndPeriodChange(nextPeriod);
  };

  const copyTier = isTrialingClinician
    ? (legacyTier ?? currentPlan)
    : (currentPlan ?? legacyTier);

  const { showClinician, showPractice, isClinicianHighlighted, isPracticeHighlighted } =
    usePricingPlanColumns({ ...modalState, layout: 'trialEnd' });

  const {
    clinicianFeatures,
    clinicianFeaturesTitle,
    practiceFeatures,
    practiceFeaturesTitle,
  } = usePricingPlansModalFeatures(copyTier);

  const clinicianPrices = useClinicianSeatPrices();
  const practicePrices = usePracticeSeatPrices();

  const {
    hasTeam,
    isTeamStripeManaged,
    isNonAdminTeamMember,
    isLoadingTeamDetails,
    isTeamDataReady,
  } = useCombinedQueriesTeamGeneralInfo();

  const teamRoleUnresolved = !!isLoadingTeamDetails || isTeamDataReady === false;
  const settledNonAdmin = !!isNonAdminTeamMember && !teamRoleUnresolved;
  const shouldDisableUpgradeForNonStripeManagedTeam =
    hasTeam && !isTeamStripeManaged && !teamRoleUnresolved;
  const clinicianCtaIsUpgrade = !isClinicianStandardPlan || isTrialingClinician;
  const practiceCtaIsUpgrade = !isPracticeStandardPlan || isTrialingClinician;
  const upgradeDisabledTooltip = settledNonAdmin
    ? formatMessage(pricingPlansModalMessages.contactTeamAdminToUpgrade)
    : shouldDisableUpgradeForNonStripeManagedTeam
      ? formatMessage(pricingPlansModalMessages.contactSalesToUpgrade)
      : undefined;

  const selectSeatsHelper = formatMessage(
    practiceSeatUpgradeCardMessages.selectSeatsNextStep
  );

  const maxSaveBadge = showComplimentaryOffer ? (
    <Badge size="xs" color="green" data-testid="trial-end-save-extra-badge">
      {formatMessage(clinicianUpgradeCheckoutMessages.saveExtra, {
        percent: COMPLIMENTARY_TRIAL_END_YEARLY_DISCOUNT_PERCENT,
      })}
    </Badge>
  ) : null;

  const clinicianStandardPrice = isYearly
    ? clinicianPrices.standard.yearlyPerMonth
    : clinicianPrices.standard.monthlyPerMonth;
  const clinicianMaxPrice = isYearly
    ? clinicianPrices.max.yearlyPerMonth
    : clinicianPrices.max.monthlyPerMonth;

  const clinicianStandardLabel = isYearly
    ? formatMessage(heidi2UpgradeCheckoutMessages.standardSeatWithOriginalPrice, {
        price: clinicianStandardPrice,
        originalPrice: clinicianPrices.standard.monthlyPerMonth,
        strong,
        original,
      })
    : formatMessage(heidi2UpgradeCheckoutMessages.standardSeat, {
        price: clinicianStandardPrice,
        strong,
      });

  const clinicianMaxLabel = isYearly
    ? formatMessage(trialEndPricingPlanCardsMessages.maxSeatWithOriginalPrice, {
        price: clinicianMaxPrice,
        originalPrice: clinicianPrices.max.monthlyPerMonth,
        strong,
        original,
      })
    : formatMessage(heidi2UpgradeCheckoutMessages.maxSeat, {
        price: clinicianMaxPrice,
        strong,
      });

  const practiceStandardLabel = formatMessage(
    practiceSeatUpgradeCardMessages.standardSeat,
    { price: practicePrices.standardSeatPrice, strong }
  );
  const practiceMaxLabel = formatMessage(practiceSeatUpgradeCardMessages.maxSeat, {
    price: practicePrices.maxSeatPrice,
    strong,
  });

  const visibleCardCount = [showClinician, showPractice].filter(Boolean).length;
  // Solo Practice (Clinician Max / Practice / Scribe Team) matches the seat-upgrade
  // card: full-width row, blue audience badge, pro CTA. Beside Clinician it stays
  // the secondary outline card.
  const isPracticeOnly = visibleCardCount === 1 && showPractice;

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-4"
      data-testid="trial-end-pricing-plan-cards"
    >
      {showComplimentaryOffer ? (
        <ComplimentaryTrialEndOfferCard
          cycleEnd={yearlyMaxOffer.cycleEnd}
          conditionsHref={ports.billingUrls.getUsageLimitsHelpUrl(locale)}
        />
      ) : null}

      <div
        data-testid="pricing-plan-row"
        className={cn(
          'grid w-full min-w-0 grid-cols-1 gap-4 md:items-stretch',
          visibleCardCount === 2 && 'md:grid-cols-2'
        )}
      >
        {showClinician ? (
          <TrialEndDualSeatPlanCard
            data-testid="trial-end-clinician-card"
            audience={formatMessage(
              clinicianUpgradeCheckoutMessages.forIndividualClinicians
            )}
            audienceColor="blue"
            planName={formatMessage(pricingPlansModalMessages.planNameClinicianPro)}
            isHighlighted={isClinicianHighlighted}
            periodControl={
              <PlanPeriodTabs
                value={billingPeriod}
                onValueChange={handlePeriodChange}
                size="sm"
                variant={isClinicianHighlighted ? 'proTier' : 'default'}
              />
            }
            standardSeat={{
              label: clinicianStandardLabel,
              description: formatMessage(
                heidi2UpgradeCheckoutMessages.standardSeatDescription
              ),
            }}
            maxSeat={{
              label: clinicianMaxLabel,
              description: formatMessage(
                heidi2UpgradeCheckoutMessages.maxSeatDescription
              ),
              badge: isYearly ? maxSaveBadge : null,
            }}
            ctaText={trialEndClinicianProPlanCtaLabel}
            ctaIcon={<TierProFilled size={16} aria-hidden />}
            ctaVariant="pro"
            ctaHelper={selectSeatsHelper}
            onCtaClick={isAppleCompliant ? undefined : onTrialEndClinicianCtaClick}
            isCtaLoading={isPending}
            isCtaDisabled={
              ((teamRoleUnresolved || settledNonAdmin) && clinicianCtaIsUpgrade) ||
              (currentPlan !== SubscriptionTier.CLINICIAN_PRO &&
                shouldDisableUpgradeForNonStripeManagedTeam)
            }
            ctaDisabledTooltip={upgradeDisabledTooltip}
            featuresTitle={<FormattedMessage {...clinicianFeaturesTitle} />}
            features={clinicianFeatures}
          />
        ) : null}

        {showPractice ? (
          <TrialEndDualSeatPlanCard
            data-testid="trial-end-practice-card"
            audience={formatMessage(practiceSeatUpgradeCardMessages.forCareTeams)}
            audienceColor={isPracticeOnly ? 'blue' : 'neutral'}
            planName={formatMessage(practiceSeatUpgradeCardMessages.practice)}
            isHighlighted={isPracticeHighlighted}
            isPractice={!isPracticeOnly}
            periodControl={
              <YearlyOnlyPill
                label={formatMessage(practiceSeatUpgradeCardMessages.yearlyOnly)}
              />
            }
            standardSeat={{
              label: practiceStandardLabel,
              description: formatMessage(
                practiceSeatUpgradeCardMessages.standardSeatDescription
              ),
            }}
            maxSeat={{
              label: practiceMaxLabel,
              description: formatMessage(
                practiceSeatUpgradeCardMessages.maxSeatDescription
              ),
              badge: maxSaveBadge,
            }}
            ctaText={clinicianProPlusPlanCtaLabel}
            ctaIcon={<TierPracticeFilled size={16} aria-hidden />}
            ctaVariant={isPracticeOnly ? 'pro' : 'outline'}
            ctaHelper={selectSeatsHelper}
            onCtaClick={isAppleCompliant ? undefined : onTrialEndPracticeCtaClick}
            isCtaLoading={isPending}
            isCtaDisabled={
              ((teamRoleUnresolved || settledNonAdmin) && practiceCtaIsUpgrade) ||
              (currentPlan !== SubscriptionTier.CLINICIAN_PRO_PLUS &&
                shouldDisableUpgradeForNonStripeManagedTeam)
            }
            ctaDisabledTooltip={upgradeDisabledTooltip}
            featuresTitle={<FormattedMessage {...practiceFeaturesTitle} />}
            features={practiceFeatures}
          />
        ) : null}
      </div>

      {isAppleCompliant ? (
        <div className="flex w-full justify-center py-4">
          <AppleIapInfoText className="text-center text-text-secondary" />
        </div>
      ) : (
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...pricingPlansModalMessages.salesTaxAndTermsNote} />
          </TypographyCaption>
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              {...pricingPlansModalMessages.talkToSales}
              values={{
                link: (
                  <Link
                    href={ports.billingUrls.GET_IN_TOUCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="inline"
                    size="inherit"
                    onClick={onTalkToSalesClick}
                  >
                    <FormattedMessage {...pricingPlansModalMessages.talkToSalesCta} />
                    <ExternalLink className="size-3" aria-hidden />
                  </Link>
                ),
              }}
            />
          </TypographyCaption>
        </div>
      )}
    </div>
  );
}
